import { Button, Select, SelectOptionType } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Card, Col, Row } from 'reactstrap';

import { requestReliquidationCourses } from '../../../api/requests/courseRegistration';
import { getCreditsByPeriod } from '../../../api/requests/periods';
import Base from '../../../components/base/Base';
import { useBase } from '../../../components/base/BaseProvider';
import SelectedSections from '../../../components/courseEnrollment/SelectedSections';
import TopBar from '../../../components/topBar/TopBar';
import { useCourseSelectionState } from '../../../hooks/useCourseSelectionState';
import { TranslationsKeys } from '../../../locales/translations';
import CourseList from '../../courseRegistration/parts/CourseList';
import WeekCalendar from '../../courseRegistration/parts/WeekCalendar';
import CreditCards from './parts/CreditCards';
import StudentInfoCard from './parts/StudentInfoCard';
import SubmitReliquidation from './parts/SubmitReliquidation';

export default function CreditReliquidationPage() {
  return (
    <Base>
      <CreditReliquidationContent />
    </Base>
  );
}

function CreditReliquidationContent() {
  const { t } = useTranslation(TranslationsKeys.PETITIONS);
  const history = useHistory();
  const { careers, periods } = useBase();

  const {
    selectedSections,
    selectedCareer,
    creditsPeriod,
    removeSection,
    setSelectedCareer,
    setCreditsPeriod,
    resetCourseSelection,
  } = useCourseSelectionState();

  const [
    selectedStudyPlanEnrollment,
    setSelectedStudyPlanEnrollment,
  ] = useState<SelectOptionType | undefined>(undefined);

  const text = useMemo(() => {
    const prefix = 'creditReliquidation';
    return {
      title: t(`${prefix}.title`),
      subtitle: t(`${prefix}.subtitle`),
      description: t(`${prefix}.description`),
      selectProgram: t(`${prefix}.selectProgram`),
      back: t(`${prefix}.back`),
      selectCourses: t(`${prefix}.selectCourses`),
    };
  }, [t]);

  const careerOptions = useMemo<SelectOptionType[]>(
    () =>
      careers.map((c) => ({
        value: c.studyPlanEnrollmentId,
        label: c.name,
      })),
    [careers],
  );

  const loadCreditsInfo = useCallback(
    async (studyPlanEnrollmentId: number) => {
      const { data } = await getCreditsByPeriod(String(studyPlanEnrollmentId));
      if (data) {
        setCreditsPeriod({
          creditMin: data.creditMin ?? 0,
          creditMax: data.creditMax ?? 0,
          creditConfigurationEnabled: data.creditConfigurationEnabled ?? false,
          creditsContracted: data.creditsContracted ?? null,
        });
      }
    },
    [setCreditsPeriod],
  );

  const handleCareerChange = useCallback(
    (option: SelectOptionType | null) => {
      setSelectedStudyPlanEnrollment(option ?? undefined);
      resetCourseSelection();

      if (option) {
        const career = careers.find(
          (c) => c.studyPlanEnrollmentId === option.value,
        );
        if (career) {
          setSelectedCareer({
            studyPlanEnrollmentId: career.studyPlanEnrollmentId,
            career: {
              id: career.id,
              name: career.name,
            },
            mention: null,
          });
          loadCreditsInfo(career.studyPlanEnrollmentId);
        }
      } else {
        setSelectedCareer(null);
      }
    },
    [careers, setSelectedCareer, resetCourseSelection, loadCreditsInfo],
  );

  useEffect(() => {
    if (careerOptions.length === 1 && !selectedStudyPlanEnrollment) {
      handleCareerChange(careerOptions[0]);
    }
  }, [careerOptions, selectedStudyPlanEnrollment, handleCareerChange]);

  const handleBack = () => {
    resetCourseSelection();
    history.push('/petitions/current');
  };

  const currentPeriod = useMemo(() => {
    if (periods && periods.length > 0) {
      return periods[0];
    }
    return null;
  }, [periods]);

  return (
    <>
      <TopBar title={text.title} />
      <Card className="p-4 mx-3">
        <div className="mb-4">
          <Button text={text.back} outlined size="sm" onClick={handleBack} />
        </div>

        <h6 className="fs-20 text-uppercase fw-700 tc-primary">
          {text.subtitle}
        </h6>
        <p className="fs-18 text-light mb-4">{text.description}</p>

        <Row className="mb-4">
          <Col md={6}>
            <Select
              label={text.selectProgram}
              name="studyPlanEnrollment"
              options={careerOptions}
              value={selectedStudyPlanEnrollment}
              onChange={handleCareerChange}
              isClearable={false}
            />
          </Col>
        </Row>

        {selectedCareer && (
          <>
            <StudentInfoCard
              career={careers.find(
                (c) =>
                  c.studyPlanEnrollmentId ===
                  selectedCareer.studyPlanEnrollmentId,
              )}
              period={currentPeriod}
              creditsContracted={creditsPeriod.creditsContracted}
            />

            <CreditCards
              creditMin={creditsPeriod.creditMin}
              creditsContracted={creditsPeriod.creditsContracted}
              selectedSections={selectedSections}
            />

            <p className="fs-16 text-dark mb-3 mt-4">{text.selectCourses}</p>

            <Row className="py-4">
              <Col xs={12} lg={5} className="mb-4">
                <CourseList fetchCourses={requestReliquidationCourses} />
              </Col>
              <Col xs={12} lg={7}>
                <WeekCalendar />
                <SelectedSections
                  selectedSections={selectedSections}
                  removeSection={removeSection}
                />
              </Col>
            </Row>

            <SubmitReliquidation
              selectedSections={selectedSections}
              creditsPeriod={creditsPeriod}
              studyPlanEnrollmentId={selectedCareer.studyPlanEnrollmentId}
              periodId={currentPeriod?.id}
            />
          </>
        )}
      </Card>
    </>
  );
}
